/**
 * The advisory lane on a farmer's card: three tabs that read the Python
 * sidecar (server/advisory.ts) and never invent a number.
 *
 *   Water      depth to water at the farm, what a dug well and a borewell would
 *              need, the CGWB sites it rests on, and the drill verdict
 *   VRA map    the variable-rate urea map for one khasra and one crop, drawn on
 *              the farm map and listed as walkable sectors
 *   Crops      for each crop the farmer grows: the fertiliser dose for Madhya
 *              Pradesh, the pests of that crop with their life cycles and
 *              windows, and what the next days' weather changes
 *
 * Under the tabs, "Send advisory pack" turns the three into the farmer's
 * WhatsApp pack in Hindi (the bot's own map and dose pictures, the sectors
 * text, a water card, a crop card), previewed here and sent to the phone the
 * officer recorded - directly through the business number when the server
 * has the keys, else by opening WhatsApp with the text prefilled.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CropEntry, Holder, HolderFarm, OnboardingEvent } from '@shared/model';
import { CROPS, SEASONS, prettyPhone } from '@shared/model';
import { apiGet, apiPost, type Loadable } from '../api/client';
import { useSession } from '../state/SessionContext';
import { StateBlock } from './StateBlock';

type Tab = 'water' | 'vra' | 'crops';

interface Well { village: string | null; block: string | null; km: number; pre_monsoon: [number, number] | null; post_monsoon: [number, number] | null; trend_m_per_yr: number | null; n_pre: number }
interface Water {
  model?: { depth_m: Record<string, number | null>; mae_m?: number; generated?: string };
  wells: Well[];
  guidance: {
    basis: string; pre_monsoon_level_m: number | null; post_monsoon_level_m: number | null; fall_m_per_yr: number; ten_year_drop_m: number; water_expected_m?: number;
    dug_well?: { feasible: boolean; dig_to_m: number; note: string }; borewell?: { suggested_drill_m: number; safe_limit_m: number; note: string }; rule?: string;
  };
  decision?: { verdict: string; probability: number | null; state_base_rate?: number | null; local_evidence?: { wells: number; successful: number } | null; note?: string };
  fracture_band_m?: { p25: number; median: number; p75: number; n: number; note: string };
  exploration_wells?: { km: number; yield_lps: number | null; depth_m?: number | null; village?: string }[];
  note?: string; cached_at?: string;
}
interface VraFeature { type: 'Feature'; geometry: GeoJSON.Geometry; properties: { zone: number; name: string; name_hi: string; colour: string; ndvi_mean: number; urea_kg_ha: number; dap_kg_ha: number; mop_kg_ha: number; area_ha: number; area_acres: number; urea_kg_zone: number } }
interface Vra {
  available: boolean; uniform: boolean; zones: number; geojson: { type: 'FeatureCollection'; features: VraFeature[] };
  totals: { urea_kg: number; uniform_urea_kg: number; dap_kg: number; mop_kg: number }; dose: { where?: string; n_kg_ha?: number; p2o5_kg_ha?: number; k2o_kg_ha?: number; urea_kg_ha?: number; dap_kg_ha?: number; mop_kg_ha?: number; basis?: string };
  newest_scene?: string; window?: string; ndvi_field_mean?: number; field_area_acres?: number; rule?: string; source?: string;
  sectors?: { sectors: Record<string, unknown>[] }; cached_at?: string;
}
interface Pest {
  name: string; name_hi: string; sci: string; kind: string; window_das: [number, number]; months: [number, number];
  cycle: Record<string, [number, number] | string>; weather: { temp_c?: [number, number]; rh_min?: number; rain?: string }; etl: string; scout: string; action: string;
  risk?: { level: 'high' | 'watch' | 'low'; favourable_days: number; in_season: boolean; reasons: string[] };
}
interface CropAdvice {
  crop: string; crop_key: string;
  dose: { available: boolean; crop?: string; crop_hi?: string; match?: string; row?: { where?: string; regime?: string; variant?: string }; field_dose?: { n: number | null; p2o5: number | null; k2o: number | null; s?: number | null }; unit?: string; timing?: string; extras?: string[]; steps?: { text?: string; rule?: string; note?: string }[]; products?: { kg_per_ha?: Record<string, number>; kg_per_acre?: Record<string, number>; kg_for_field?: Record<string, number>; area_ha?: number; p_source?: string }; caveat?: string; note?: string };
  pests: Pest[]; pest_note?: string;
  weather?: { source?: string; daily?: { date: string; t_mean: number | null; t_max: number | null; t_min: number | null; rh_mean: number | null; rain_mm: number; wind_max: number | null }[]; spray_windows?: { time: string; conditions: string; note_hi?: string }[]; available?: boolean; error?: string };
  actions?: { kind: string; hold: boolean; text: string; text_hi?: string }[]; cached_at?: string;
}

const MONTH = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const range = (v: [number, number] | string | undefined): string => (Array.isArray(v) ? (v[0] === v[1] ? `${v[0]}` : `${v[0]}–${v[1]}`) : v ? String(v) : '—');
const m1 = (v: number | null | undefined): string => (v == null ? '—' : v.toFixed(1));

/** The season now: rabi from October to March, kharif otherwise. */
const seasonNow = (): 'rabi' | 'kharif' => { const m = new Date().getMonth() + 1; return m >= 10 || m <= 3 ? 'rabi' : 'kharif'; };

/**
 * Crop choices in the order an officer wants them: the farmer's crops of the
 * current season first (urea top-dressing is a standing-crop decision), then
 * the farmer's other crops, then every other crop of the list.
 */
function cropOptions(crops: CropEntry[]): { crop: string; season: string | null }[] {
  const now = seasonNow();
  const mine = [...crops.filter((c) => c.season === now), ...crops.filter((c) => c.season !== now)];
  const seen = new Set<string>();
  const out: { crop: string; season: string | null }[] = [];
  for (const c of mine) if (!seen.has(c.crop)) { seen.add(c.crop); out.push({ crop: c.crop, season: c.season }); }
  for (const c of [...CROPS[now], ...CROPS.rabi, ...CROPS.kharif]) if (!seen.has(c)) { seen.add(c); out.push({ crop: c, season: null }); }
  return out;
}

/** The farmer's one-acre plot in the programme block, when the card is opened from there. */
export interface PlotRef { holder_id: string; rank: number; khasras: string[]; pin: [number, number]; acres: number; clipped: boolean }

export function AdvisoryPanel({ holder, crops, plot, onZones, onEvents }: { holder: Holder; crops: CropEntry[]; plot?: PlotRef; onZones?: (fc: GeoJSON.FeatureCollection | null, pid: string | null) => void; onEvents?: (events: OnboardingEvent[]) => void }): JSX.Element {
  const [tab, setTab] = useState<Tab>('water');
  const [pid, setPid] = useState(plot ? 'plot' : holder.primary_pid);
  // In the programme block the farmer's one-acre plot is the default field:
  // its own pin for water, its own boundary for the VRA map, one acre for the dose.
  const plotFarm: HolderFarm | null = plot
    ? { pid: 'plot', khasra: `plot ${plot.rank} (${plot.khasras.join(' + ')})`, acres: plot.acres, pin: plot.pin, shared: false, in_block: true, co_owners: [] }
    : null;
  const farm: HolderFarm = pid === 'plot' && plotFarm ? plotFarm : holder.farms.find((f) => f.pid === pid) ?? plotFarm ?? holder.farms[0]!;
  useEffect(() => { setPid(plot ? 'plot' : holder.primary_pid); }, [holder.id, holder.primary_pid, plot?.holder_id, plot]);

  return (
    <section>
      <h4>Advisory</h4>
      <div className="tabs" style={{ marginBottom: 10 }}>
        {([['water', 'Water'], ['vra', 'Fertiliser map (VRA)'], ['crops', 'Crop advisory']] as [Tab, string][]).map(([t, label]) => (
          <button key={t} type="button" className={tab === t ? 'tab is-active' : 'tab'} onClick={() => setTab(t)}>{label}</button>
        ))}
      </div>
      {(holder.farms.length > 1 || plotFarm) && tab !== 'crops' ? (
        <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          {plotFarm ? 'Field' : 'Khasra'}{' '}
          <select className="btn-ghost" value={farm.pid} onChange={(e) => setPid(e.target.value)}>
            {plotFarm ? <option value="plot">1-acre plot · farm {plot!.rank}</option> : null}
            {holder.farms.map((f) => <option key={f.pid} value={f.pid}>khasra {f.khasra} · {f.acres.toFixed(2)} ac</option>)}
          </select>
        </label>
      ) : null}
      {tab === 'water' ? <WaterTab farm={farm} /> : tab === 'vra' ? <VraTab farm={farm} crops={crops} holder={holder} onZones={onZones} /> : <CropsTab holder={holder} farm={farm} crops={crops} />}
      <PackBlock holder={holder} farm={farm} crops={crops} onEvents={onEvents} />
    </section>
  );
}

// ------------------------------------------------------------------ water

function WaterTab({ farm }: { farm: HolderFarm }): JSX.Element {
  const [state, setState] = useState<Loadable<Water>>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void apiGet<Water>(`/api/advisory/water?lat=${farm.pin[0]}&lon=${farm.pin[1]}`).then((r) => live && setState(r));
    return () => { live = false; };
  }, [farm.pid, farm.pin]);
  if (state.status !== 'ready') return <StateBlock state={state} title="Water depth" />;
  const w = state.data;
  const g = w.guidance;
  const verdict = w.decision?.verdict ?? 'unknown';
  const vClass = verdict === 'go' ? 'ok' : verdict === 'caution' ? 'warn' : verdict === 'avoid' ? 'bad' : 'muted';
  return (
    <div className="adv">
      <div className="stat-row">
        <div className="stat"><span className="stat__label">Water now (post-monsoon)</span><span className="stat__value">{m1(g.post_monsoon_level_m)} m</span></div>
        <div className="stat"><span className="stat__label">Dry season (May)</span><span className="stat__value">{m1(g.water_expected_m ?? g.pre_monsoon_level_m)} m</span></div>
        <div className="stat"><span className="stat__label">Falling</span><span className="stat__value">{g.fall_m_per_yr.toFixed(2)} m/yr</span></div>
        <div className="stat"><span className="stat__label">Borewell verdict</span><span className={`stat__value ${vClass}`}>{verdict}{w.decision?.probability != null ? ` · ${Math.round(w.decision.probability * 100)}%` : ''}</span></div>
      </div>
      {g.dug_well ? (
        <div className={`notice ${g.dug_well.feasible ? 'notice--ok' : 'notice--warn'}`} style={{ marginBottom: 8 }}>
          <strong>Dug well · dig to {g.dug_well.dig_to_m} m{g.dug_well.feasible ? '' : ' — not practical here'}.</strong> {g.dug_well.note}
        </div>
      ) : null}
      {g.borewell ? (
        <div className="notice notice--ok" style={{ marginBottom: 8 }}>
          <strong>Borewell · safe limit about {g.borewell.safe_limit_m} m.</strong> {g.borewell.note}
        </div>
      ) : null}
      {w.fracture_band_m ? <p className="fc__note">Water-bearing fractures in CGWB test wells within 20 km: median {w.fracture_band_m.median} m (middle half {w.fracture_band_m.p25}–{w.fracture_band_m.p75} m, {w.fracture_band_m.n} readings).</p> : null}
      <p className="fc__note">Basis: {g.basis}.{w.model?.depth_m ? ` Modelled grid: pre-monsoon ${m1(w.model.depth_m.pre_monsoon)} m, monsoon ${m1(w.model.depth_m.monsoon)} m, post-monsoon ${m1(w.model.depth_m.post_monsoon)} m (MAE ${w.model.mae_m ?? '?'} m).` : ''}</p>
      {w.wells.length ? (
        <table className="vtable" style={{ marginTop: 6 }}>
          <thead><tr><th>CGWB site</th><th className="num">km</th><th className="num">Pre-monsoon</th><th className="num">Post-monsoon</th><th className="num">Trend</th></tr></thead>
          <tbody>
            {w.wells.slice(0, 6).map((x, i) => (
              <tr key={i}>
                <td>{x.village ?? '—'}{x.block ? <span className="muted"> · {x.block}</span> : null}</td>
                <td className="num">{x.km}</td>
                <td className="num">{x.pre_monsoon ? `${x.pre_monsoon[1]} m (${x.pre_monsoon[0]})` : '—'}</td>
                <td className="num">{x.post_monsoon ? `${x.post_monsoon[1]} m (${x.post_monsoon[0]})` : '—'}</td>
                <td className={`num ${x.trend_m_per_yr != null && x.trend_m_per_yr > 0.3 ? 'warn' : ''}`}>{x.trend_m_per_yr != null ? `${x.trend_m_per_yr > 0 ? '+' : ''}${x.trend_m_per_yr} m/yr` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="fc__note">No CGWB monitoring site within 25 km; the modelled grid alone is shown.</p>}
      {w.decision?.local_evidence ? <p className="fc__note">{w.decision.local_evidence.successful} of {w.decision.local_evidence.wells} CGWB test wells within 15 km gave at least 1 litre/second.</p> : null}
      <p className="fc__note">{w.note}{w.cached_at ? ` Read ${w.cached_at.slice(0, 10)}.` : ''}</p>
    </div>
  );
}

// ------------------------------------------------------------------ VRA

function VraTab({ farm, crops, holder, onZones }: { farm: HolderFarm; crops: CropEntry[]; holder: Holder; onZones?: (fc: GeoJSON.FeatureCollection | null, pid: string | null) => void }): JSX.Element {
  // The current season's crops first (urea top-dressing is what VRA moves, and
  // that is a standing-crop decision), then the farmer's other crops, then the rest.
  const options = useMemo(() => cropOptions(crops).map((o) => o.crop), [crops]);
  const [crop, setCrop] = useState(options[0] ?? 'Wheat');
  const [zones, setZones] = useState<3 | 5>(3);
  const [state, setState] = useState<Loadable<Vra>>({ status: 'idle' });
  useEffect(() => { setState({ status: 'idle' }); onZones?.(null, null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [farm.pid, holder.id]);

  const run = async (refresh = false): Promise<void> => {
    setState({ status: 'loading' });
    const body = farm.pid === 'plot' ? { holder_id: holder.id, plot: true, crop, zones, refresh } : { pid: farm.pid, crop, zones, refresh };
    const r = await apiPost<Vra>('/api/advisory/vra', body);
    setState(r);
    if (r.status === 'ready' && r.data?.geojson) onZones?.(r.data.geojson as GeoJSON.FeatureCollection, farm.pid);
  };

  return (
    <div className="adv">
      <div className="fc__row" style={{ marginBottom: 8 }}>
        <select className="btn-ghost" value={crop} onChange={(e) => setCrop(e.target.value)}>
          {options.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="btn-ghost" value={zones} onChange={(e) => setZones(Number(e.target.value) as 3 | 5)}>
          <option value={3}>3 zones</option><option value={5}>5 zones</option>
        </select>
        <button type="button" className="btn-primary" disabled={state.status === 'loading'} onClick={() => void run()}>{state.status === 'loading' ? 'Reading the satellite…' : 'Make the map'}</button>
        {state.status === 'ready' ? <button type="button" className="btn-ghost" onClick={() => void run(true)}>Refresh</button> : null}
      </div>
      {state.status === 'loading' ? <p className="fc__note">Reading the last 45 days of 10 m imagery over khasra {farm.khasra} — about a minute.</p> : null}
      {state.status !== 'ready' ? <StateBlock state={state} title="Fertiliser map" /> : (() => {
        const v = state.data;
        const feats = v.geojson?.features ?? [];
        const saving = v.totals ? Math.round(v.totals.uniform_urea_kg - v.totals.urea_kg) : 0;
        return (
          <>
            <div className="stat-row">
              <div className="stat"><span className="stat__label">Field</span><span className="stat__value">{v.field_area_acres?.toFixed(2)} ac</span></div>
              <div className="stat"><span className="stat__label">Urea (variable)</span><span className="stat__value">{Math.round(v.totals.urea_kg)} kg</span></div>
              <div className="stat"><span className="stat__label">Urea (uniform)</span><span className="stat__value">{Math.round(v.totals.uniform_urea_kg)} kg</span></div>
              <div className="stat"><span className="stat__label">DAP / MOP basal</span><span className="stat__value">{Math.round(v.totals.dap_kg)} / {Math.round(v.totals.mop_kg)} kg</span></div>
            </div>
            {v.uniform ? <div className="notice notice--warn" style={{ marginBottom: 8 }}>Too few clear pixels inside this khasra to split it: one uniform dose is shown.</div> : null}
            <table className="vtable">
              <thead><tr><th>Zone</th><th className="num">NDVI</th><th className="num">Urea kg/ha</th><th className="num">Acres</th><th className="num">Urea kg</th></tr></thead>
              <tbody>
                {feats.map((f) => (
                  <tr key={f.properties.zone}>
                    <td><span className="seg-chip" style={{ background: f.properties.colour }} />{f.properties.name} <span className="muted">{f.properties.name_hi}</span></td>
                    <td className="num">{f.properties.ndvi_mean.toFixed(2)}</td>
                    <td className="num">{Math.round(f.properties.urea_kg_ha)}</td>
                    <td className="num">{f.properties.area_acres.toFixed(2)}</td>
                    <td className="num">{Math.round(f.properties.urea_kg_zone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {v.sectors?.sectors?.length ? (
              <details style={{ marginTop: 8 }}>
                <summary className="muted" style={{ cursor: 'pointer', fontSize: 12 }}>Walkable sectors ({v.sectors.sectors.length})</summary>
                <ul className="fc__events">
                  {v.sectors.sectors.map((s, i) => (
                    <li key={i}>
                      <span><span className="seg-chip" style={{ background: String(s.colour ?? '#888') }} />{String(s.label ?? s.name ?? `Sector ${i + 1}`)} {s.name_hi ? <span className="muted">{String(s.name_hi)}</span> : null}</span>
                      <span>{Object.entries(s).filter(([k, val]) => typeof val === 'number' && !['zone'].includes(k)).map(([k, val]) => `${k.replace(/_/g, ' ')} ${typeof val === 'number' ? (Number.isInteger(val) ? val : (val as number).toFixed(1)) : ''}`).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <p className="fc__note">
              Dose basis: {v.dose?.where ?? '—'} ({v.dose?.basis ?? ''}) — N {v.dose?.n_kg_ha} · P₂O₅ {v.dose?.p2o5_kg_ha} · K₂O {v.dose?.k2o_kg_ha} kg/ha. Newest clear scene {v.newest_scene ?? '—'}, window {v.window ?? '—'}, field NDVI {v.ndvi_field_mean?.toFixed(2)}.
              {saving > 0 ? ` Variable rate moves ${saving} kg of urea from the strong patches to the weak ones; the field total stays the same.` : ''} Only urea moves between zones; phosphate and potash stay basal.
            </p>
            {v.cached_at ? <p className="fc__note">Read {v.cached_at.slice(0, 10)}; refresh for today's imagery.</p> : null}
          </>
        );
      })()}
    </div>
  );
}

// ------------------------------------------------------------------ crops

function CropsTab({ holder, farm, crops }: { holder: Holder; farm: HolderFarm; crops: CropEntry[] }): JSX.Element {
  const list = crops.length ? crops : holder.crop_last_season ? [{ season: 'rabi' as const, crop: holder.crop_last_season }] : [];
  const [sel, setSel] = useState<CropEntry | null>(list[0] ?? null);
  useEffect(() => { setSel(list[0] ?? null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [holder.id, crops.length]);
  const [state, setState] = useState<Loadable<CropAdvice>>({ status: 'idle' });
  // In the block the dose is for the one-acre plot; elsewhere for everything the holder is named on.
  const areaHa = Math.round(((farm.pid === 'plot' ? farm.acres : holder.acres) / 2.47105) * 100) / 100;
  useEffect(() => {
    if (!sel) { setState({ status: 'idle' }); return; }
    let live = true;
    setState({ status: 'loading' });
    void apiGet<CropAdvice>(`/api/advisory/crop?crop=${encodeURIComponent(sel.crop)}&lat=${farm.pin[0]}&lon=${farm.pin[1]}&area_ha=${areaHa}&season=${sel.season}`).then((r) => live && setState(r));
    return () => { live = false; };
  }, [sel, farm.pin, areaHa]);

  if (!list.length) return <p className="fc__note">Record the crops this farmer grows (above) to get the crop-wise advisory.</p>;
  return (
    <div className="adv">
      <div className="chips" style={{ marginBottom: 10 }}>
        {list.map((c) => (
          <button key={`${c.season}-${c.crop}`} type="button" className={`chip-toggle ${sel?.crop === c.crop && sel?.season === c.season ? 'is-on' : ''}`} style={{ padding: '4px 9px', fontSize: 12 }} onClick={() => setSel(c)}>
            {c.crop} <span className="muted">· {SEASONS.find((s) => s.id === c.season)?.label}</span>
          </button>
        ))}
      </div>
      {state.status !== 'ready' ? <StateBlock state={state} title="Crop advisory" /> : <CropAdviceView a={state.data} areaHa={areaHa} />}
    </div>
  );
}

function CropAdviceView({ a, areaHa }: { a: CropAdvice; areaHa: number }): JSX.Element {
  const d = a.dose;
  const fd = d?.field_dose;
  const pa = d?.products?.kg_per_acre;
  const pf = d?.products?.kg_for_field;
  return (
    <>
      <h5 className="adv__h">Fertiliser dose · {d?.crop_hi ?? ''} {d?.crop?.replace(/_/g, ' ')}</h5>
      {d?.available && fd ? (
        <>
          <div className="stat-row">
            <div className="stat"><span className="stat__label">N</span><span className="stat__value">{fd.n ?? '—'}</span></div>
            <div className="stat"><span className="stat__label">P₂O₅</span><span className="stat__value">{fd.p2o5 ?? '—'}</span></div>
            <div className="stat"><span className="stat__label">K₂O</span><span className="stat__value">{fd.k2o ?? '—'}</span></div>
            {fd.s ? <div className="stat"><span className="stat__label">S</span><span className="stat__value">{fd.s}</span></div> : null}
            <div className="stat"><span className="stat__label">unit</span><span className="stat__value" style={{ fontSize: 13 }}>{d.unit ?? 'kg/ha'}</span></div>
          </div>
          {pa ? (
            <table className="vtable">
              <thead><tr><th>Bags</th>{Object.keys(pa).map((k) => <th key={k} className="num">{k}</th>)}</tr></thead>
              <tbody>
                <tr><td>per acre</td>{Object.values(pa).map((v, i) => <td key={i} className="num">{Math.round(v)} kg</td>)}</tr>
                {pf ? <tr><td>this holder&rsquo;s {areaHa} ha</td>{Object.values(pf).map((v, i) => <td key={i} className="num">{Math.round(v)} kg</td>)}</tr> : null}
              </tbody>
            </table>
          ) : null}
          {d.timing ? <p className="fc__note"><strong>When:</strong> {d.timing}</p> : null}
          {d.extras?.length ? <ul className="adv__list">{d.extras.map((x, i) => <li key={i}>{x}</li>)}</ul> : null}
          {d.caveat ? <div className="notice notice--warn" style={{ marginTop: 6 }}>{d.caveat}</div> : null}
          <p className="fc__note">Package: {d.row?.where}{d.row?.regime ? ` · ${d.row.regime}` : ''}{d.row?.variant ? ` · ${d.row.variant.replace(/_/g, ' ')}` : ''}. Without a soil test the printed state dose is shown; a Soil Health Card corrects N by organic carbon and P, K by their tests.</p>
        </>
      ) : <p className="fc__note">{d?.note ?? 'No dose row for this crop.'}</p>}

      <h5 className="adv__h">What the next five days change</h5>
      {a.weather?.daily?.length ? (
        <table className="vtable">
          <thead><tr><th>Day</th><th className="num">Max</th><th className="num">Min</th><th className="num">RH</th><th className="num">Rain</th><th className="num">Wind</th></tr></thead>
          <tbody>
            {a.weather.daily.slice(0, 5).map((x) => (
              <tr key={x.date}><td>{x.date.slice(5)}</td><td className="num">{m1(x.t_max)}°</td><td className="num">{m1(x.t_min)}°</td><td className="num">{x.rh_mean ?? '—'}%</td><td className={`num ${x.rain_mm >= 10 ? 'warn' : ''}`}>{x.rain_mm} mm</td><td className="num">{x.wind_max ?? '—'}</td></tr>
            ))}
          </tbody>
        </table>
      ) : <p className="fc__note">{a.weather?.error ?? 'Forecast not reachable.'}</p>}
      {a.actions?.length ? (
        <ul className="adv__list">
          {a.actions.map((x, i) => <li key={i} className={x.hold ? 'warn' : ''}>{x.text}{x.text_hi ? <span className="muted"> — {x.text_hi}</span> : null}</li>)}
        </ul>
      ) : null}
      {a.weather?.spray_windows?.length ? (
        <p className="fc__note">Spray windows: {a.weather.spray_windows.slice(0, 6).map((w) => `${w.time.slice(5, 10)} ${w.time.slice(11, 16)}`).join(', ')}{a.weather.spray_windows.length > 6 ? ` +${a.weather.spray_windows.length - 6}` : ''} — low wind, no rain, under 35 °C.</p>
      ) : null}

      <h5 className="adv__h">Pests and diseases of this crop — cycles and windows</h5>
      {a.pests.length ? a.pests.map((p) => (
        <details key={p.name} className="adv__pest" open={p.risk?.level === 'high'}>
          <summary>
            <span className={`pill pill--${p.risk?.level === 'high' ? 'low' : p.risk?.level === 'watch' ? 'medium' : 'none'}`}>{p.risk?.level ?? '—'}</span>{' '}
            <strong>{p.name}</strong> <span className="muted">{p.name_hi} · {p.kind} · {MONTH[p.months[0]]}–{MONTH[p.months[1]]}, {p.window_das[0]}–{p.window_das[1]} days after sowing</span>
          </summary>
          <dl>
            <dt>Life cycle</dt>
            <dd>
              {(['egg_d', 'larva_d', 'nymph_d', 'pupa_d', 'adult_d'] as const).filter((k) => p.cycle[k]).map((k) => `${k.replace('_d', '')} ${range(p.cycle[k])} d`).join(' → ')}
              {p.cycle.total_d ? ` · one generation ${range(p.cycle.total_d)} days` : ''}{p.cycle.generations ? ` · ${p.cycle.generations}` : ''}
            </dd>
            <dt>Weather it likes</dt>
            <dd>{p.weather.temp_c ? `${p.weather.temp_c[0]}–${p.weather.temp_c[1]} °C` : '—'}{p.weather.rh_min ? `, RH ≥ ${p.weather.rh_min}%` : ''}{p.weather.rain === 'L' ? ', dry spells' : p.weather.rain === 'H' ? ', rain / wet leaves' : ''}{p.risk ? ` — ${p.risk.reasons.join('; ')}` : ''}</dd>
            <dt>Scout for</dt><dd>{p.scout}</dd>
            <dt>Act at</dt><dd>{p.etl}</dd>
            <dt>Action</dt><dd>{p.action}</dd>
          </dl>
        </details>
      )) : <p className="fc__note">No pest calendar for this crop yet.</p>}
      <p className="fc__note">{a.pest_note}</p>
    </>
  );
}

// ------------------------------------------------------------------ the pack

interface PackCard { text?: string; map_caption?: string; dose_caption?: string; available?: boolean; error?: string; basemap?: boolean; newest_scene?: string | null; uniform?: boolean }
interface PackView {
  id: string; holder_id: string; pid: string; khasra: string; crop: string; season: string; built_at: string; by: string;
  phone: string | null; number: string; direct: boolean;
  cards: { vra: PackCard | null; water: PackCard | null; crop: PackCard | null };
  images: { map: string | null; dose: string | null };
  sources: string[]; parts: string[]; text_all: string; wa_link: string | null; sent_at?: string | null; sent_to?: string | null;
}
const CARD_LABEL: Record<'vra' | 'water' | 'crop', string> = { vra: 'Fertiliser map · sectors A, B, C', water: 'Water card', crop: 'Crop card' };

function PackBlock({ holder, farm, crops, onEvents }: { holder: Holder; farm: HolderFarm; crops: CropEntry[]; onEvents?: (events: OnboardingEvent[]) => void }): JSX.Element {
  const { account } = useSession();
  const options = useMemo(() => {
    const o = cropOptions(crops);
    // A farmer with no recorded crops gets last season's crop from the record first, if there is one.
    if (!crops.length && holder.crop_last_season && !o.some((x) => x.crop === holder.crop_last_season)) o.unshift({ crop: holder.crop_last_season, season: null });
    return o;
  }, [crops, holder.crop_last_season]);
  const [crop, setCrop] = useState(options[0]?.crop ?? 'Wheat');
  const [pid, setPid] = useState(farm.pid);
  const [state, setState] = useState<Loadable<PackView>>({ status: 'idle' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testTo, setTestTo] = useState('');
  useEffect(() => { setState({ status: 'idle' }); setMsg(null); setCrop(options[0]?.crop ?? 'Wheat'); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [holder.id]);
  useEffect(() => { setPid(farm.pid); }, [farm.pid]);
  const chosen = holder.farms.find((f) => f.pid === pid) ?? farm;

  const build = async (): Promise<void> => {
    setMsg(null);
    setState({ status: 'loading' });
    const season = options.find((o) => o.crop === crop)?.season ?? '';
    setState(await apiPost<PackView>('/api/advisory/pack', { holder_id: holder.id, pid: chosen.pid, crop, season }));
  };

  const send = async (to?: string): Promise<void> => {
    if (state.status !== 'ready') return;
    setBusy(true);
    setMsg(null);
    const r = await apiPost<{ sent: boolean; to: string; parts: string[]; events: OnboardingEvent[] }>(`/api/advisory/pack/${state.data.id}/send`, to ? { to } : {});
    setBusy(false);
    if (r.status === 'ready' && r.data) {
      setMsg({ ok: true, text: `Sent to ${prettyPhone(r.data.to)}: ${r.data.parts.join(', ')}.` });
      onEvents?.(r.data.events);
      if (!to) setState({ status: 'ready', data: { ...state.data, sent_at: new Date().toISOString(), sent_to: r.data.to } });
    } else {
      setMsg({ ok: false, text: r.status === 'error' || r.status === 'not_configured' ? r.message : 'Not sent.' });
    }
  };

  const mark = (): void => {
    if (state.status !== 'ready') return;
    void apiPost<{ marked: boolean; events: OnboardingEvent[] }>(`/api/advisory/pack/${state.data.id}/mark`, {}).then((r) => {
      if (r.status === 'ready' && r.data) onEvents?.(r.data.events);
    });
  };

  const copy = async (text: string): Promise<void> => {
    try { await navigator.clipboard.writeText(text); setMsg({ ok: true, text: 'Copied.' }); } catch { setMsg({ ok: false, text: 'Clipboard refused; select the text instead.' }); }
  };

  const fileName = (what: string): string => `${what}-${chosen.khasra.replace(/[^\w]+/g, '-')}.jpg`;

  return (
    <div className="adv pack">
      <h5 className="adv__h">Send advisory pack · WhatsApp, Hindi</h5>
      <p className="fc__note">
        The farmer&rsquo;s pack: the fertiliser map and the dose table as pictures (the ones the AI Bhoomi bot sends), the sectors text (A, B, C…), a water card and this crop&rsquo;s card.
        It is built from the three tabs above; every card names its source and date.
      </p>
      <div className="fc__row" style={{ marginTop: 8 }}>
        {holder.farms.length > 1 ? (
          <select className="btn-ghost" value={chosen.pid} onChange={(e) => setPid(e.target.value)} disabled={state.status === 'loading'}>
            {holder.farms.map((f) => <option key={f.pid} value={f.pid}>Khasra {f.khasra} · {f.acres.toFixed(2)} ac</option>)}
          </select>
        ) : <span className="muted">Khasra {chosen.khasra}</span>}
        <select className="btn-ghost" value={crop} onChange={(e) => setCrop(e.target.value)} disabled={state.status === 'loading'}>
          {options.map((o) => <option key={o.crop} value={o.crop}>{o.crop}{o.season ? ` · ${SEASONS.find((s) => s.id === o.season)?.label ?? o.season}` : ''}</option>)}
        </select>
        <button type="button" className="btn-primary" disabled={state.status === 'loading'} onClick={() => void build()}>
          {state.status === 'loading' ? 'Building the pack…' : state.status === 'ready' ? 'Build again' : 'Preview pack'}
        </button>
      </div>
      {state.status === 'loading' ? <p className="fc__note">Drawing the pack for khasra {chosen.khasra} · {crop}. The satellite map is read fresh when today&rsquo;s is not cached yet — about a minute.</p> : null}
      {state.status === 'error' || state.status === 'not_configured' ? <div className="notice notice--bad" style={{ marginTop: 8 }}>{state.message}</div> : null}
      {state.status === 'ready' ? (() => {
        const p = state.data;
        const noPhone = !p.phone;
        return (
          <>
            {p.images.map || p.images.dose ? (
              <div className="pack__imgs">
                {p.images.map ? <a href={p.images.map} target="_blank" rel="noreferrer" title="Open the map picture"><img src={p.images.map} alt="Fertiliser map picture: the khasra on satellite imagery with its sectors lettered" /></a> : null}
                {p.images.dose ? <a href={p.images.dose} target="_blank" rel="noreferrer" title="Open the dose picture"><img src={p.images.dose} alt="Dose picture: urea, DAP and MOP kilograms per sector" /></a> : null}
              </div>
            ) : null}
            {(['vra', 'water', 'crop'] as const).map((k) => {
              const c = p.cards[k];
              return (
                <div key={k} className="pack__card">
                  <div className="pack__card-head">
                    <span>{CARD_LABEL[k]}</span>
                    {c?.text ? <button type="button" className="btn-ghost" style={{ padding: '2px 7px', fontSize: 11 }} onClick={() => void copy(c.text ?? '')}>Copy</button> : null}
                  </div>
                  {c?.text ? <div className="fc__msg" style={{ marginTop: 4 }}>{c.text}</div> : <div className="notice notice--warn" style={{ marginTop: 4 }}>Not in this pack: {c?.error ?? 'no answer from the sidecar'}.</div>}
                </div>
              );
            })}
            <p className="fc__note">Sources: {p.sources.join(' · ')}. Built {p.built_at.slice(0, 16).replace('T', ' ')} by {p.by.split('@')[0]}.{p.sent_at ? ` Sent ${p.sent_at.slice(0, 16).replace('T', ' ')} to ${prettyPhone(p.sent_to ?? '')}.` : ''}</p>
            {noPhone ? (
              <div className="notice notice--warn" style={{ marginTop: 8 }}>No phone recorded for this farmer. Save it in the Field officer section below to send; the pictures and texts can still be downloaded and copied.</div>
            ) : null}
            <div className="fc__buttons">
              {!noPhone && p.direct ? (
                <button type="button" className="btn-primary" disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : `Send to ${prettyPhone(p.phone ?? '')}`}</button>
              ) : null}
              {!noPhone && !p.direct && p.wa_link ? (
                <a className="btn-primary" href={p.wa_link} target="_blank" rel="noreferrer" onClick={mark} title="Opens the farmer's chat with the three texts prefilled; attach the two pictures by hand">Open in WhatsApp</a>
              ) : null}
              {p.images.map ? <a className="btn-line" href={p.images.map} download={fileName('khaad-naksha')}>Download map</a> : null}
              {p.images.dose ? <a className="btn-line" href={p.images.dose} download={fileName('khaad-matra')}>Download dose table</a> : null}
              <button type="button" className="btn-line" onClick={() => void copy(p.text_all)}>Copy all text</button>
            </div>
            {!noPhone && !p.direct ? <p className="fc__note">Direct sending is not connected on this server: WhatsApp opens with the three texts prefilled, and the two downloaded pictures are attached by hand.</p> : null}
            {account?.admin && p.direct ? (
              <div className="fc__controls" style={{ marginTop: 8 }}>
                <input value={testTo} onChange={(e) => setTestTo(e.target.value.replace(/[^\d+ ]/g, ''))} placeholder="Test send to another number (administrators), e.g. 98xxxxxxxx" inputMode="tel" />
                <button type="button" className="btn-line" disabled={busy || testTo.replace(/\D/g, '').length < 10} onClick={() => void send(testTo)}>Test send</button>
              </div>
            ) : null}
            {msg ? <p className={`fc__note ${msg.ok ? 'fc__note--ok' : 'fc__note--bad'}`}>{msg.text}</p> : null}
          </>
        );
      })() : null}
    </div>
  );
}
